/**
 * Rewards Catalog Page
 * Manage rewards that customers can redeem with their points
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Modal, ModalBody, ModalFooter, Badge, toast } from '../components/ui';
import {
  rewardCatalogApi,
  squareApi,
  Reward,
  CreateRewardData,
  RewardCategory,
  RewardStatus,
  CustomerTier,
  RewardType,
  DiscountType,
  BogoConfig,
  TimeRestriction,
  SquareCatalogItem,
  handleApiError,
} from '../lib/api';

const REWARD_TEMPLATES = [
  {
    icon: '🍔',
    name: 'Free Meal',
    description: 'Free meal up to a certain value',
    category: 'FOOD' as RewardCategory,
    example: 'Free Any Item',
    suggestedPoints: 200,
  },
  {
    icon: '☕',
    name: 'Free Drink',
    description: 'Free beverage of choice',
    category: 'BEVERAGE' as RewardCategory,
    example: 'Free Coffee or Tea',
    suggestedPoints: 100,
  },
  {
    icon: '🏷️',
    name: 'Discount',
    description: 'Percentage or dollar off',
    category: 'DISCOUNT' as RewardCategory,
    example: '50% Off Next Purchase',
    suggestedPoints: 150,
  },
  {
    icon: '🎁',
    name: 'Merchandise',
    description: 'Physical product or swag',
    category: 'MERCHANDISE' as RewardCategory,
    example: 'Branded T-Shirt',
    suggestedPoints: 300,
  },
];

const CATEGORY_LABELS: Record<RewardCategory, string> = {
  FOOD: 'Food',
  BEVERAGE: 'Beverage',
  DISCOUNT: 'Discount',
  MERCHANDISE: 'Merchandise',
  EXPERIENCE: 'Experience',
  CUSTOM: 'Custom',
};

const TIER_LABELS: Record<CustomerTier, string> = {
  BRONZE: 'Buffies Member',
  SILVER: 'Buffies Crew',
  GOLD: 'Buffies VIP',
  PLATINUM: 'Buffies Icon / Legend',
};

const REWARD_TYPE_LABELS: Record<RewardType, string> = {
  POINTS_DISCOUNT: 'Points Discount',
  PERCENTAGE_DISCOUNT: 'Percentage Discount',
  FREE_ITEM: 'Free Item',
  BOGO: 'Buy One Get One',
  ITEM_DISCOUNT: 'Item Discount',
  TIME_BASED: 'Time-Based',
};

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export const RewardsCatalogPage: React.FC = () => {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<typeof REWARD_TEMPLATES[0] | null>(null);
  const [formData, setFormData] = useState<CreateRewardData>({
    name: '',
    description: '',
    category: 'CUSTOM' as RewardCategory,
    pointsCost: 0,
    stockQuantity: undefined,
    maxPerCustomer: undefined,
    minTier: 'BRONZE' as CustomerTier,
    imageUrl: '',
    startDate: undefined,
    endDate: undefined,
    rewardType: undefined,
    discountType: undefined,
    discountValue: undefined,
    maxDiscountCap: undefined,
    bogoConfig: undefined,
    timeRestriction: undefined,
    maxUsesPerDay: undefined,
    linkedItemId: undefined,
  });

  // Enhanced reward type editor state
  const [selectedRewardType, setSelectedRewardType] = useState<RewardType | ''>('');
  const [bogoConfig, setBogoConfig] = useState<BogoConfig>({ buyQuantity: 1, getQuantity: 1, freeItemValue: undefined });
  const [timeRestriction, setTimeRestriction] = useState<TimeRestriction>({
    startHour: 9,
    endHour: 17,
    daysOfWeek: [1, 2, 3, 4, 5],
    discountType: 'DOLLAR',
    discountValue: 0,
  });
  const [catalogItems, setCatalogItems] = useState<SquareCatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    loadRewards();
  }, []);

  // Load Square catalog items for item selectors
  const loadCatalogItems = async () => {
    if (catalogItems.length > 0) return;
    setCatalogLoading(true);
    try {
      const res = await squareApi.getSquareCatalogItems();
      setCatalogItems(res.data.data);
    } catch {
      // Catalog items not available
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadRewards = async () => {
    setIsLoading(true);
    try {
      const response = await rewardCatalogApi.getAll({ includeInactive: true });
      setRewards(response.data.data);
    } catch (error) {
      console.error('Failed to load rewards:', error);
      toast.error('Failed to load rewards');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template: typeof REWARD_TEMPLATES[0]) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      category: template.category,
      pointsCost: template.suggestedPoints,
      name: template.example,
      description: template.description,
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'CUSTOM' as RewardCategory,
      pointsCost: 0,
      stockQuantity: undefined,
      maxPerCustomer: undefined,
      minTier: 'BRONZE' as CustomerTier,
      imageUrl: '',
      startDate: undefined,
      endDate: undefined,
      rewardType: undefined,
      discountType: undefined,
      discountValue: undefined,
      maxDiscountCap: undefined,
      bogoConfig: undefined,
      timeRestriction: undefined,
      maxUsesPerDay: undefined,
      linkedItemId: undefined,
    });
    setSelectedRewardType('');
    setBogoConfig({ buyQuantity: 1, getQuantity: 1, freeItemValue: undefined });
    setTimeRestriction({ startHour: 9, endHour: 17, daysOfWeek: [1, 2, 3, 4, 5], discountType: 'DOLLAR', discountValue: 0 });
    setSelectedTemplate(null);
    setCreateError(null);
  };

  const handleCreateReward = async () => {
    setCreateError(null);

    // Validate required fields
    if (!formData.name.trim()) {
      setCreateError('Reward name is required.');
      return;
    }
    if (!formData.pointsCost || formData.pointsCost <= 0) {
      setCreateError('Points cost must be greater than 0.');
      return;
    }

    setIsCreating(true);
    try {
      const payload: CreateRewardData = {
        ...formData,
        stockQuantity: formData.stockQuantity || undefined,
        maxPerCustomer: formData.maxPerCustomer || undefined,
      };
      // Add reward type specific fields
      if (selectedRewardType) {
        payload.rewardType = selectedRewardType;
        if (selectedRewardType === 'BOGO') {
          payload.bogoConfig = bogoConfig;
        } else if (selectedRewardType === 'TIME_BASED') {
          payload.timeRestriction = timeRestriction;
        }
      }
      const response = await rewardCatalogApi.create(payload);
      setRewards((prev) => [response.data.data, ...prev]);
      setShowCreateModal(false);
      resetForm();
      toast.success('Reward created successfully!');
    } catch (error) {
      const apiError = handleApiError(error);
      setCreateError(apiError.message);
      toast.error('Failed to create reward');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditReward = async () => {
    if (!editingReward) return;

    setCreateError(null);
    setIsCreating(true);
    try {
      const payload = { ...formData };
      if (selectedRewardType) {
        payload.rewardType = selectedRewardType;
        if (selectedRewardType === 'BOGO') {
          payload.bogoConfig = bogoConfig;
        } else if (selectedRewardType === 'TIME_BASED') {
          payload.timeRestriction = timeRestriction;
        }
      }
      const response = await rewardCatalogApi.update(editingReward.id, payload);
      setRewards((prev) =>
        prev.map((r) => (r.id === editingReward.id ? response.data.data : r))
      );
      setShowEditModal(false);
      setEditingReward(null);
      resetForm();
      toast.success('Reward updated successfully!');
    } catch (error) {
      const apiError = handleApiError(error);
      setCreateError(apiError.message);
      toast.error('Failed to update reward');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteReward = async (id: string) => {
    if (!confirm('Are you sure you want to archive this reward?')) return;

    try {
      await rewardCatalogApi.delete(id);
      setRewards((prev) => prev.filter((r) => r.id !== id));
      toast.success('Reward archived successfully!');
    } catch (error) {
      toast.error('Failed to archive reward');
    }
  };

  const handleUpdateStock = async (id: string, currentStock: number | null) => {
    const newStock = prompt('Enter new stock quantity:', currentStock?.toString() || '0');
    if (newStock === null) return;

    const quantity = parseInt(newStock, 10);
    if (isNaN(quantity) || quantity < 0) {
      toast.error('Please enter a valid number');
      return;
    }

    try {
      const response = await rewardCatalogApi.updateStock(id, quantity);
      setRewards((prev) =>
        prev.map((r) => (r.id === id ? response.data.data : r))
      );
      toast.success('Stock updated successfully!');
    } catch (error) {
      toast.error('Failed to update stock');
    }
  };

  const openEditModal = (reward: Reward) => {
    setEditingReward(reward);
    const rwd = reward as any;
    setFormData({
      name: reward.name,
      description: reward.description || '',
      category: reward.category,
      pointsCost: reward.pointsCost,
      stockQuantity: reward.stockQuantity || undefined,
      maxPerCustomer: reward.maxPerCustomer || undefined,
      minTier: reward.minTier,
      imageUrl: reward.imageUrl || '',
      startDate: reward.startDate ? reward.startDate.split('T')[0] : undefined,
      endDate: reward.endDate ? reward.endDate.split('T')[0] : undefined,
      rewardType: rwd.rewardType || undefined,
      discountType: rwd.discountType || undefined,
      discountValue: rwd.discountValue || undefined,
      maxDiscountCap: rwd.maxDiscountCap || undefined,
      bogoConfig: rwd.bogoConfig || undefined,
      timeRestriction: rwd.timeRestriction || undefined,
      maxUsesPerDay: rwd.maxUsesPerDay || undefined,
      linkedItemId: rwd.linkedItemId || undefined,
    });
    setSelectedRewardType(rwd.rewardType || '');
    if (rwd.bogoConfig) setBogoConfig(rwd.bogoConfig);
    if (rwd.timeRestriction) setTimeRestriction(rwd.timeRestriction);
    setShowEditModal(true);
  };

  const getStatusBadge = (status: RewardStatus) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="success">Active</Badge>;
      case 'INACTIVE':
        return <Badge variant="default">Inactive</Badge>;
      case 'OUT_OF_STOCK':
        return <Badge variant="warning">Out of Stock</Badge>;
      case 'ARCHIVED':
        return <Badge variant="error">Archived</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Rewards Catalog</h1>
        <p className="text-gray-600 mt-1">
          Create and manage rewards that customers can redeem with their points
        </p>
      </div>

      {/* Actions */}
      <div className="mb-6 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {rewards.length} {rewards.length === 1 ? 'reward' : 'rewards'}
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          ➕ Create Reward
        </Button>
      </div>

      {/* Rewards Table */}
      {isLoading ? (
        <Card className="p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-orange-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading rewards...</p>
        </Card>
      ) : rewards.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-600 mb-4">No rewards yet. Create your first reward!</p>
          <Button onClick={() => setShowCreateModal(true)}>
            ➕ Create Your First Reward
          </Button>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reward
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Min Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Redemptions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rewards.map((reward) => (
                  <tr key={reward.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {reward.name}
                          </div>
                          {reward.description && (
                            <div className="text-sm text-gray-500">
                              {reward.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {CATEGORY_LABELS[reward.category]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-orange-600">
                        {reward.pointsCost} pts
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {TIER_LABELS[reward.minTier]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleUpdateStock(reward.id, reward.stockQuantity ?? null)}
                        className="text-sm text-orange-500 hover:text-orange-600"
                      >
                        {reward.stockQuantity !== null && reward.stockQuantity !== undefined
                          ? `${reward.stockQuantity} items`
                          : 'Unlimited'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {reward.totalRedemptions}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(reward.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEditModal(reward)}
                        className="text-orange-500 hover:text-orange-700 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteReward(reward.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Archive
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create Reward"
      >
        <ModalBody>
          {!selectedTemplate && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Choose a template to get started
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {REWARD_TEMPLATES.map((template) => (
                  <button
                    key={template.name}
                    onClick={() => handleTemplateSelect(template)}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors text-left"
                  >
                    <div className="text-2xl mb-2">{template.icon}</div>
                    <div className="text-sm font-medium text-gray-900">
                      {template.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {template.description}
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setSelectedTemplate({} as any)}
                  className="text-sm text-orange-600 hover:text-orange-700"
                >
                  Or create from scratch →
                </button>
              </div>
            </div>
          )}

          {selectedTemplate && (
            <div className="space-y-4">
              {createError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {createError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Free Coffee"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the reward"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value as RewardCategory })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Points Cost *
                  </label>
                  <Input
                    type="number"
                    value={formData.pointsCost}
                    onChange={(e) =>
                      setFormData({ ...formData, pointsCost: parseInt(e.target.value) || 0 })
                    }
                    min="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Quantity (optional)
                  </label>
                  <Input
                    type="number"
                    value={formData.stockQuantity || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        stockQuantity: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Unlimited"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Per Customer (optional)
                  </label>
                  <Input
                    type="number"
                    value={formData.maxPerCustomer || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxPerCustomer: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Tier Required
                </label>
                <select
                  value={formData.minTier}
                  onChange={(e) =>
                    setFormData({ ...formData, minTier: e.target.value as CustomerTier })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {Object.entries(TIER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL (optional)
                </label>
                <Input
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              {/* Reward Type Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Reward Type Configuration</h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reward Type
                  </label>
                  <select
                    value={selectedRewardType}
                    onChange={(e) => {
                      const rt = e.target.value as RewardType | '';
                      setSelectedRewardType(rt);
                      setFormData({ ...formData, rewardType: rt || undefined, discountType: undefined, discountValue: undefined, maxDiscountCap: undefined, linkedItemId: undefined });
                      if (rt === 'ITEM_DISCOUNT') loadCatalogItems();
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">-- Select Type (optional) --</option>
                    {Object.entries(REWARD_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* Points Discount fields */}
                {selectedRewardType === 'POINTS_DISCOUNT' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Value ($)
                    </label>
                    <Input
                      type="number"
                      value={formData.discountValue || ''}
                      onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || undefined, discountType: 'DOLLAR' })}
                      placeholder="e.g., 5.00"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-gray-500 mt-1">Dollar amount off when redeemed with points</p>
                  </div>
                )}

                {/* Percentage Discount fields */}
                {selectedRewardType === 'PERCENTAGE_DISCOUNT' && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount Percentage (%)
                      </label>
                      <Input
                        type="number"
                        value={formData.discountValue || ''}
                        onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || undefined, discountType: 'PERCENTAGE' })}
                        placeholder="e.g., 20"
                        min="1"
                        max="100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Discount Cap ($, optional)
                      </label>
                      <Input
                        type="number"
                        value={formData.maxDiscountCap || ''}
                        onChange={(e) => setFormData({ ...formData, maxDiscountCap: parseFloat(e.target.value) || undefined })}
                        placeholder="e.g., 25.00"
                        min="0"
                        step="0.01"
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum dollar amount the discount can reach</p>
                    </div>
                  </div>
                )}

                {/* Free Item fields */}
                {selectedRewardType === 'FREE_ITEM' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item Price ($)
                    </label>
                    <Input
                      type="number"
                      value={formData.discountValue || ''}
                      onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || undefined, discountType: 'DOLLAR' })}
                      placeholder="e.g., 12.00"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-gray-500 mt-1">Value of the free item</p>
                  </div>
                )}

                {/* BOGO fields */}
                {selectedRewardType === 'BOGO' && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Buy Quantity
                        </label>
                        <Input
                          type="number"
                          value={bogoConfig.buyQuantity}
                          onChange={(e) => setBogoConfig({ ...bogoConfig, buyQuantity: parseInt(e.target.value) || 1 })}
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Get Quantity (Free)
                        </label>
                        <Input
                          type="number"
                          value={bogoConfig.getQuantity}
                          onChange={(e) => setBogoConfig({ ...bogoConfig, getQuantity: parseInt(e.target.value) || 1 })}
                          min="1"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Free Item Value ($, optional)
                      </label>
                      <Input
                        type="number"
                        value={bogoConfig.freeItemValue || ''}
                        onChange={(e) => setBogoConfig({ ...bogoConfig, freeItemValue: parseFloat(e.target.value) || undefined })}
                        placeholder="Max value of free item"
                        min="0"
                        step="0.01"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave blank for equal-or-lesser value</p>
                    </div>
                  </div>
                )}

                {/* Item Discount fields */}
                {selectedRewardType === 'ITEM_DISCOUNT' && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount Value ($)
                      </label>
                      <Input
                        type="number"
                        value={formData.discountValue || ''}
                        onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || undefined, discountType: 'DOLLAR' })}
                        placeholder="e.g., 3.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Linked Item
                      </label>
                      {catalogLoading ? (
                        <p className="text-xs text-gray-500">Loading catalog items...</p>
                      ) : catalogItems.length > 0 ? (
                        <select
                          value={formData.linkedItemId || ''}
                          onChange={(e) => setFormData({ ...formData, linkedItemId: e.target.value || undefined })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="">-- Select Item --</option>
                          {catalogItems.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name} {item.price ? `($${item.price.toFixed(2)})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div>
                          <Input
                            value={formData.linkedItemId || ''}
                            onChange={(e) => setFormData({ ...formData, linkedItemId: e.target.value || undefined })}
                            placeholder="Enter item ID or name"
                          />
                          <p className="text-xs text-gray-500 mt-1">Connect Square POS to select from catalog</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Time-Based fields */}
                {selectedRewardType === 'TIME_BASED' && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Hour
                        </label>
                        <select
                          value={timeRestriction.startHour}
                          onChange={(e) => setTimeRestriction({ ...timeRestriction, startHour: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>
                              {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Hour
                        </label>
                        <select
                          value={timeRestriction.endHour}
                          onChange={(e) => setTimeRestriction({ ...timeRestriction, endHour: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>
                              {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Days of Week
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {DAYS_OF_WEEK.map((day) => (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => {
                              const days = timeRestriction.daysOfWeek.includes(day.value)
                                ? timeRestriction.daysOfWeek.filter((d) => d !== day.value)
                                : [...timeRestriction.daysOfWeek, day.value];
                              setTimeRestriction({ ...timeRestriction, daysOfWeek: days });
                            }}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                              timeRestriction.daysOfWeek.includes(day.value)
                                ? 'bg-orange-500 text-white border-orange-500'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-orange-400'
                            }`}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Discount Type
                        </label>
                        <select
                          value={timeRestriction.discountType}
                          onChange={(e) => setTimeRestriction({ ...timeRestriction, discountType: e.target.value as DiscountType })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="DOLLAR">Dollar ($)</option>
                          <option value="PERCENTAGE">Percentage (%)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Discount Value
                        </label>
                        <Input
                          type="number"
                          value={timeRestriction.discountValue || ''}
                          onChange={(e) => setTimeRestriction({ ...timeRestriction, discountValue: parseFloat(e.target.value) || 0 })}
                          placeholder={timeRestriction.discountType === 'DOLLAR' ? 'e.g., 5.00' : 'e.g., 20'}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Max Uses Per Day - shown for all reward types */}
                {selectedRewardType && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Uses Per Day (optional)
                    </label>
                    <Input
                      type="number"
                      value={formData.maxUsesPerDay || ''}
                      onChange={(e) => setFormData({ ...formData, maxUsesPerDay: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="Unlimited"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Limit how many times this reward can be redeemed per day</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateReward}
            disabled={isCreating || !selectedTemplate}
          >
            {isCreating ? 'Creating...' : 'Create Reward'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingReward(null);
          resetForm();
        }}
        title="Edit Reward"
      >
        <ModalBody>
          <div className="space-y-4">
            {createError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {createError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reward Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value as RewardCategory })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points Cost *
                </label>
                <Input
                  type="number"
                  value={formData.pointsCost}
                  onChange={(e) =>
                    setFormData({ ...formData, pointsCost: parseInt(e.target.value) || 0 })
                  }
                  min="1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stock Quantity
                </label>
                <Input
                  type="number"
                  value={formData.stockQuantity || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stockQuantity: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  placeholder="Unlimited"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Per Customer
                </label>
                <Input
                  type="number"
                  value={formData.maxPerCustomer || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxPerCustomer: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  placeholder="Unlimited"
                  min="1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Tier Required
              </label>
              <select
                value={formData.minTier}
                onChange={(e) =>
                  setFormData({ ...formData, minTier: e.target.value as CustomerTier })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {Object.entries(TIER_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Reward Type Section (Edit) */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Reward Type Configuration</h4>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Type
                </label>
                <select
                  value={selectedRewardType}
                  onChange={(e) => {
                    const rt = e.target.value as RewardType | '';
                    setSelectedRewardType(rt);
                    setFormData({ ...formData, rewardType: rt || undefined, discountType: undefined, discountValue: undefined, maxDiscountCap: undefined, linkedItemId: undefined });
                    if (rt === 'ITEM_DISCOUNT') loadCatalogItems();
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">-- Select Type (optional) --</option>
                  {Object.entries(REWARD_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Points Discount fields */}
              {selectedRewardType === 'POINTS_DISCOUNT' && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Value ($)
                  </label>
                  <Input
                    type="number"
                    value={formData.discountValue || ''}
                    onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || undefined, discountType: 'DOLLAR' })}
                    placeholder="e.g., 5.00"
                    min="0"
                    step="0.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">Dollar amount off when redeemed with points</p>
                </div>
              )}

              {/* Percentage Discount fields */}
              {selectedRewardType === 'PERCENTAGE_DISCOUNT' && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Percentage (%)
                    </label>
                    <Input
                      type="number"
                      value={formData.discountValue || ''}
                      onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || undefined, discountType: 'PERCENTAGE' })}
                      placeholder="e.g., 20"
                      min="1"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Discount Cap ($, optional)
                    </label>
                    <Input
                      type="number"
                      value={formData.maxDiscountCap || ''}
                      onChange={(e) => setFormData({ ...formData, maxDiscountCap: parseFloat(e.target.value) || undefined })}
                      placeholder="e.g., 25.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              )}

              {/* Free Item fields */}
              {selectedRewardType === 'FREE_ITEM' && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Price ($)
                  </label>
                  <Input
                    type="number"
                    value={formData.discountValue || ''}
                    onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || undefined, discountType: 'DOLLAR' })}
                    placeholder="e.g., 12.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              )}

              {/* BOGO fields */}
              {selectedRewardType === 'BOGO' && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Buy Quantity
                      </label>
                      <Input
                        type="number"
                        value={bogoConfig.buyQuantity}
                        onChange={(e) => setBogoConfig({ ...bogoConfig, buyQuantity: parseInt(e.target.value) || 1 })}
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Get Quantity (Free)
                      </label>
                      <Input
                        type="number"
                        value={bogoConfig.getQuantity}
                        onChange={(e) => setBogoConfig({ ...bogoConfig, getQuantity: parseInt(e.target.value) || 1 })}
                        min="1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Free Item Value ($, optional)
                    </label>
                    <Input
                      type="number"
                      value={bogoConfig.freeItemValue || ''}
                      onChange={(e) => setBogoConfig({ ...bogoConfig, freeItemValue: parseFloat(e.target.value) || undefined })}
                      placeholder="Max value of free item"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              )}

              {/* Item Discount fields */}
              {selectedRewardType === 'ITEM_DISCOUNT' && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Value ($)
                    </label>
                    <Input
                      type="number"
                      value={formData.discountValue || ''}
                      onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || undefined, discountType: 'DOLLAR' })}
                      placeholder="e.g., 3.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Linked Item
                    </label>
                    {catalogLoading ? (
                      <p className="text-xs text-gray-500">Loading catalog items...</p>
                    ) : catalogItems.length > 0 ? (
                      <select
                        value={formData.linkedItemId || ''}
                        onChange={(e) => setFormData({ ...formData, linkedItemId: e.target.value || undefined })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">-- Select Item --</option>
                        {catalogItems.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} {item.price ? `($${item.price.toFixed(2)})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div>
                        <Input
                          value={formData.linkedItemId || ''}
                          onChange={(e) => setFormData({ ...formData, linkedItemId: e.target.value || undefined })}
                          placeholder="Enter item ID or name"
                        />
                        <p className="text-xs text-gray-500 mt-1">Connect Square POS to select from catalog</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Time-Based fields */}
              {selectedRewardType === 'TIME_BASED' && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Hour
                      </label>
                      <select
                        value={timeRestriction.startHour}
                        onChange={(e) => setTimeRestriction({ ...timeRestriction, startHour: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Hour
                      </label>
                      <select
                        value={timeRestriction.endHour}
                        onChange={(e) => setTimeRestriction({ ...timeRestriction, endHour: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Days of Week
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS_OF_WEEK.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => {
                            const days = timeRestriction.daysOfWeek.includes(day.value)
                              ? timeRestriction.daysOfWeek.filter((d) => d !== day.value)
                              : [...timeRestriction.daysOfWeek, day.value];
                            setTimeRestriction({ ...timeRestriction, daysOfWeek: days });
                          }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                            timeRestriction.daysOfWeek.includes(day.value)
                              ? 'bg-orange-500 text-white border-orange-500'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-orange-400'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount Type
                      </label>
                      <select
                        value={timeRestriction.discountType}
                        onChange={(e) => setTimeRestriction({ ...timeRestriction, discountType: e.target.value as DiscountType })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="DOLLAR">Dollar ($)</option>
                        <option value="PERCENTAGE">Percentage (%)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount Value
                      </label>
                      <Input
                        type="number"
                        value={timeRestriction.discountValue || ''}
                        onChange={(e) => setTimeRestriction({ ...timeRestriction, discountValue: parseFloat(e.target.value) || 0 })}
                        placeholder={timeRestriction.discountType === 'DOLLAR' ? 'e.g., 5.00' : 'e.g., 20'}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Max Uses Per Day */}
              {selectedRewardType && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Uses Per Day (optional)
                  </label>
                  <Input
                    type="number"
                    value={formData.maxUsesPerDay || ''}
                    onChange={(e) => setFormData({ ...formData, maxUsesPerDay: e.target.value ? parseInt(e.target.value) : undefined })}
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              setEditingReward(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleEditReward} disabled={isCreating}>
            {isCreating ? 'Saving...' : 'Save Changes'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
